import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Waypoint } from 'react-waypoint';

const overflows = (element: Element) =>
  element.scrollHeight > element.clientHeight;

interface Props {
  callback: () => void;
  /**
   * How many items are loaded. The automatic check below asks once per distinct
   * count, so the list has to actually grow before it asks again.
   *
   * Required, though it accepts `undefined` for a list that hasn't loaded yet:
   * leaving it out would silently mean "nothing ever grew".
   */
  itemCount: number | undefined;
  /**
   * The element that scrolls this list, when the page isn't what scrolls — a
   * table capped with `maxHeight`, say. Omit it when the page scrolls.
   *
   * Omitting it for a list that *is* inside a capped container is a silent bug:
   * the sentinel then gets measured against the window, where it stays in view
   * no matter how far the container is scrolled, and the list asks for page
   * after page. See UserAuditLogsTab for the capped case.
   */
  scroller?: React.RefObject<HTMLElement | null>;
}

/**
 * Sentinel that loads the next page when the user reaches the end of the list.
 *
 * Two things about where to render it:
 *
 * - Inside whatever scrolls the list, so that the rows it loads push it back
 *   out of view. A sentinel that never moves cannot tell "the user reached the
 *   end" from "the user is nowhere near the end".
 * - *Above* the end-of-list footer, not last. The sentinel has no height, so as
 *   the final element it lands exactly on the scroller's bottom edge once the
 *   list is scrolled to the end — and Waypoint decides whether it is in view by
 *   comparing a whole-pixel `offsetHeight` against fractional rects, which at
 *   that exact position disagree by a rounding error. Scrolling to the end then
 *   loads nothing, at some zoom levels and not others. Keeping a real element
 *   below the sentinel puts the decision back in the range where it is
 *   unambiguous.
 */
const PaginationWaypoint: React.FC<Props> = ({
  callback,
  itemCount,
  scroller,
}) => {
  const loadedCount = itemCount ?? 0;
  const [target, setTarget] = useState<HTMLElement | Window | null>(null);

  // Counts start at zero, so -1 means "nothing requested yet".
  const requestedForRef = useRef(-1);

  // Scrolling to the end is a deliberate gesture, so it always loads. Waypoint
  // only fires on a change of position, which means this cannot repeat on its
  // own — the user has to leave the end of the list and come back.
  const load = useCallback(() => {
    requestedForRef.current = loadedCount;
    callback();
  }, [callback, loadedCount]);

  // The check below runs on its own and repeats, so it stops once it has asked
  // for these rows. Without that it would spin whenever a load came back empty,
  // and callers rebuild `loadMore` every render, so the effect re-runs every
  // render — loading from an effect with no such guard is what made this load
  // forever.
  //
  // Only this path is bounded that way. Applying it to `load` as well would
  // turn one empty response into a list that can never be scrolled again.
  const loadUnlessAlreadyAsked = useCallback(() => {
    if (requestedForRef.current === loadedCount) {
      return;
    }

    load();
  }, [load, loadedCount]);

  useEffect(() => {
    const sync = () => {
      const container = scroller?.current;
      const root = document.documentElement;

      // A capped container only scrolls once it holds more rows than it can
      // show. Until then it behaves like any other block and the page is what
      // moves, so that is what Waypoint has to watch.
      const scrolled =
        container && overflows(container) ? container : (window as Window);

      // Re-setting the same element is a no-op, so this only re-renders when
      // the list outgrows the container and it starts scrolling.
      setTarget(scrolled);

      // Nothing anywhere can be scrolled, so there is no gesture left for the
      // user and no position change for Waypoint to report. Keep loading until
      // something does scroll.
      if (scrolled === window && !overflows(root)) {
        loadUnlessAlreadyAsked();
      }
    };

    sync();

    // Zooming out fires a resize and can remove the last of the scrolling.
    window.addEventListener('resize', sync);

    return () => window.removeEventListener('resize', sync);
  }, [scroller, loadUnlessAlreadyAsked]);

  if (!target) {
    return null;
  }

  return (
    <Waypoint
      // Waypoint reads `scrollableAncestor` once, on mount, so it has to be
      // remounted when the list outgrows its container and the page stops being
      // what scrolls.
      key={target === window ? 'page' : 'container'}
      scrollableAncestor={target}
      onEnter={load}
    />
  );
};

export default PaginationWaypoint;
